import React, {
  PropsWithChildren,
  HTMLAttributes,
  useEffect,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { SpinnerIcon, SvgComponent } from '../../assets';
import { breakpointMap, BreakpointsOfTOrT, theme } from '../../theme';
import { useBreakpointMap } from '../../utils';

export type ButtonSize = 'small' | 'medium' | 'large';

export type StyledButtonProps = {
  fullWidth: boolean;
  size: BreakpointsOfTOrT<ButtonSize>;
  isLoading?: boolean;
  isRounded?: boolean;
  iconLeft?: SvgComponent;
  iconRight?: SvgComponent;
  iconFill?: string;
  iconWidth?: number;
  isFocusControlled?: boolean;
  applyFocusStyle?: boolean;
};

export const IconWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: center;
  gap: ${theme.spacings[1.5].rem};
`;

export type BaseButtonWrapperProps = StyledButtonProps & {
  isFocussed?: boolean;
};

export const BaseButtonStyles = css<StyledButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  ${({ isRounded }) =>
    isRounded
      ? `border-radius: ${theme.borderRadius.rounded} `
      : `border-radius: ${theme.borderRadius.default}`};
  border: none;
  text-align: center;
  letter-spacing: 0;
  ${({ size }) =>
    breakpointMap(
      size,
      (buttonSize) => `
        font-size: ${theme.buttons.fontSize[buttonSize]}px;
        line-height: ${theme.buttons.lineHeight[buttonSize]}px;
        min-height: ${theme.buttons.height[buttonSize]}px;
        padding: ${theme.buttons.padding[buttonSize]};
      `
    )};
  font-family: ${theme.fonts.roboto};
  font-weight: 700;
  word-wrap: nowrap;
  width: ${({ fullWidth }) => fullWidth && '100%'};
  height: 40px;
  min-width: fit-content;
  transition: color 200ms ease-in, background-color 200ms ease-in,
    border-color 200ms ease-in, fill 200ms ease-in;

  &:hover {
    text-decoration: none;
  }

  &:active,
  &:focus,
  &:focus-visible {
    outline: none;
  }

  &:disabled {
    opacity: 0.64;
    cursor: auto;
    pointer-events: none;
  }
`;

export const BaseButton = styled.button<StyledButtonProps>`
  ${BaseButtonStyles};
`;

export const BaseButtonWrapperStyles = css<StyledButtonProps>`
  display: inline-block;
  border-radius: ${theme.borderRadius.default};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
`;

export const ButtonFocusStyles = css`
  border-color: ${theme.colors.system.systemBlue};
  box-shadow: 0 0 4px 0px ${theme.colors.tints.systemBlue[3]};
`;

export const BaseButtonWrapper = styled.div<BaseButtonWrapperProps>`
  ${BaseButtonWrapperStyles};
  ${({ isFocussed }) => isFocussed && ButtonFocusStyles};
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 0 ${theme.spacings[4].rem};
`;

export type ButtonLoadingSpinnerStyleProps = {
  size: BreakpointsOfTOrT<ButtonSize>;
};

export const ButtonLoadingSpinnerStyles = css<ButtonLoadingSpinnerStyleProps>`
  ${({ size }) =>
    breakpointMap(
      size,
      (buttonSize) => css`
        width: ${theme.buttons.lineHeight[buttonSize]}px;
      `
    )}
`;

export const ButtonLoadingSpinner = styled(SpinnerIcon)`
  ${ButtonLoadingSpinnerStyles}
`;

export type ButtonProps = HTMLAttributes<HTMLButtonElement> &
  Partial<PropsWithChildren<StyledButtonProps>> & {
    disabled?: boolean;
    containerId?: string;
  };

export const Button: React.FC<ButtonProps> = ({
  disabled = false,
  fullWidth = false,
  isLoading = false,
  isRounded = false,
  size = 'medium',
  iconLeft: IconLeft,
  iconRight: IconRight,
  iconFill = '',
  iconWidth = 16,
  applyFocusStyle = false,
  isFocusControlled = false,
  children,
  ...props
}) => {
  const viewportSize = useBreakpointMap({
    xs: 'Responsive',
    sm: 'tablet',
    md: 'Desktop',
  });
  const isMobile = viewportSize !== undefined && viewportSize !== 'Desktop';
  const [isFocussed, setIsFocussed] = useState(false);

  useEffect(() => {
    if (disabled) setIsFocussed(false);
  }, [disabled]);

  return (
    <BaseButtonWrapper
      fullWidth={fullWidth}
      size={size}
      isFocussed={isFocusControlled && isMobile ? applyFocusStyle : isFocussed}
      applyFocusStyle={applyFocusStyle}
    >
      <BaseButton
        disabled={disabled || isLoading}
        fullWidth={fullWidth}
        size={size}
        onTouchStart={() => setIsFocussed(true)}
        onFocus={() => setIsFocussed(true)}
        onMouseLeave={() => setIsFocussed(false)}
        isRounded={isRounded}
        {...props}
      >
        <IconWrapper>
          {IconLeft && <IconLeft fill={iconFill} width={iconWidth} />}
          {isLoading ? <ButtonLoadingSpinner size={size} /> : children}
          {IconRight && <IconRight fill={iconFill} width={iconWidth} />}
        </IconWrapper>
      </BaseButton>
    </BaseButtonWrapper>
  );
};
